import { useSelector } from "@tanstack/react-store";
import Typography from "@vapor/react-extended/ExtendedTypography";
import { ListItem, Stack, TextField, useTheme } from "@vapor/react-material";
import Autocomplete from "@vapor/react-material/Autocomplete";
import type { ReactElement, SyntheticEvent } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type {
    Intermediary,
    Operator,
} from "../../../../../core/api/use-get-subjects/SubjectsDto";
import { useGetSubjects } from "../../../../../core/api/use-get-subjects/useGetSubjects";
import type { TaxPayer } from "../../../../../core/api/use-get-tax-payers/FulfilmentDTO";
import { useGetTaxPayers } from "../../../../../core/api/use-get-tax-payers/useGetTaxPayers";
import { store } from "../../../../../store/store";
import { taxSubmissionsStoreSlice } from "../../../../../store/taxSubmissionSearchFiltersStoreSlice";

interface TaxSubmissionsTableHeaderFilterProps {
    accordionLeft?: JSX.Element;
    accordionRight?: JSX.Element;
    openFilters: boolean;
}

export const TaxSubmissionsTableHeaderFilter = ({
    accordionLeft,
    accordionRight,
    openFilters,
}: TaxSubmissionsTableHeaderFilterProps): ReactElement => {
    const { t } = useTranslation();
    const theme = useTheme();

    const filters = useSelector(
        store,
        (state) => state.taxSubmissionsSearchFilters,
    );

    const { data: subjects, isFetching: isFetchingSubjects } = useGetSubjects();
    const { data: taxPayers, isFetching: isFetchingTaxPayers } =
        useGetTaxPayers();

    const taxPayerOptions = useMemo(() => {
        if (!taxPayers) return [];
        const selected = taxPayers.find(
            (taxPayer) => taxPayer.taxId === filters.taxPayer,
        );
        return selected
            ? [
                  selected,
                  ...taxPayers.filter((tp) => tp.taxId !== selected.taxId),
              ]
            : taxPayers;
    }, [taxPayers, filters.taxPayer]);

    const selectedTaxPayer = useMemo(
        () =>
            taxPayerOptions?.find(
                (taxPayer) => taxPayer.taxId === filters.taxPayer,
            ) ?? null,
        [filters.taxPayer, taxPayerOptions],
    );

    const operatorOptions = useMemo(() => {
        if (!subjects?.operators) return [];
        const selected = subjects.operators.find(
            (operator) => operator.id === filters.operator,
        );
        return selected
            ? [
                  selected,
                  ...subjects.operators.filter((op) => op.id !== selected.id),
              ]
            : subjects.operators;
    }, [filters.operator, subjects.operators]);

    const selectedOperator = useMemo(
        () =>
            operatorOptions?.find(
                (operator) => operator.id === filters.operator,
            ) ?? null,
        [filters.operator, operatorOptions],
    );

    const intermediaryOptions = useMemo(() => {
        if (!subjects?.intermediaries) return [];
        const selected = subjects.intermediaries.find(
            (intermediary) => intermediary.taxId === filters.intermediary,
        );
        return selected
            ? [
                  selected,
                  ...subjects.intermediaries.filter(
                      (im) => im.taxId !== selected.taxId,
                  ),
              ]
            : subjects.intermediaries;
    }, [filters.intermediary, subjects.intermediaries]);

    const selectedIntermediary = useMemo(
        () =>
            intermediaryOptions?.find(
                (intermediary) => intermediary.taxId === filters.intermediary,
            ) ?? null,
        [filters.intermediary, intermediaryOptions],
    );

    const handleTaxPayerChange = (
        _: SyntheticEvent<Element, Event>,
        value: TaxPayer | null,
    ) => {
        taxSubmissionsStoreSlice.updateValues({ taxPayer: value?.taxId });
    };

    const handleOperatorChange = (
        _: SyntheticEvent<Element, Event>,
        value: Operator | null,
    ) => {
        taxSubmissionsStoreSlice.updateValues({ operator: value?.id });
    };

    const handleIntermediaryChange = (
        _: SyntheticEvent<Element, Event>,
        value: Intermediary | null,
    ) => {
        taxSubmissionsStoreSlice.updateValues({
            intermediary: value?.taxId,
        });
    };

    return (
        <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between">
                <Stack direction="row" spacing={1}>
                    {accordionLeft}
                </Stack>
                {accordionRight}
            </Stack>
            {openFilters && (
                <Stack direction="row" spacing={2}>
                    <Autocomplete
                        disableClearable={false}
                        disabled={
                            isFetchingTaxPayers || taxPayerOptions.length === 0
                        }
                        filterOptions={(options, { inputValue }) => {
                            return options.filter((option) => {
                                const name = option.name
                                    ? option.name
                                    : `${option.firstName} ${option.lastName}`;
                                return (
                                    name
                                        .toLowerCase()
                                        .includes(inputValue.toLowerCase()) ||
                                    option.taxId
                                        .toLowerCase()
                                        .includes(inputValue.toLowerCase())
                                );
                            });
                        }}
                        loading={isFetchingTaxPayers}
                        getOptionLabel={(option: TaxPayer) =>
                            option.name
                                ? option.name
                                : `${option.firstName} ${option.lastName}`
                        }
                        isOptionEqualToValue={(option, value) => {
                            return option.taxId === value.taxId;
                        }}
                        noOptionsText={t(
                            "views.home.components.tax_submissions_table_header_filters.taxPayer.no_options_text",
                        )}
                        onChange={handleTaxPayerChange}
                        options={taxPayerOptions}
                        renderOption={(props, option) => {
                            return (
                                <ListItem {...props} key={option.taxId}>
                                    <Stack>
                                        {option.name ? (
                                            <Typography>
                                                {option.name}
                                            </Typography>
                                        ) : (
                                            <Typography>
                                                {option.firstName}{" "}
                                                {option.lastName}
                                            </Typography>
                                        )}
                                        <Typography
                                            color={
                                                theme.palette.primary
                                                    .textSubduedColor
                                            }
                                        >
                                            {option.taxId}
                                        </Typography>
                                    </Stack>
                                </ListItem>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t(
                                    "views.home.components.tax_submissions_table_header_filters.taxPayer.label",
                                )}
                                InputLabelProps={{
                                    ...params.InputLabelProps,
                                    shrink: true,
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    sx: { width: "16vw" },
                                }}
                                placeholder={t(
                                    "views.home.components.tax_submissions_table_header_filters.taxPayer.placeholder",
                                )}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        padding: 0,
                                        paddingLeft: "4px",
                                    },
                                }}
                            />
                        )}
                        style={{ width: "16vw" }}
                        value={selectedTaxPayer}
                    />
                    <Autocomplete
                        disableClearable={false}
                        disabled={
                            isFetchingSubjects || operatorOptions.length === 0
                        }
                        loading={isFetchingSubjects}
                        getOptionLabel={(option: Operator) =>
                            `${option.familyName} ${option.givenName}`
                        }
                        isOptionEqualToValue={(option, value) => {
                            return option.id === value.id;
                        }}
                        noOptionsText={t(
                            "views.home.components.tax_submissions_table_header_filters.operator.no_options_text",
                        )}
                        onChange={handleOperatorChange}
                        options={operatorOptions}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t(
                                    "views.home.components.tax_submissions_table_header_filters.operator.label",
                                )}
                                InputLabelProps={{
                                    ...params.InputLabelProps,
                                    shrink: true,
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    sx: { width: "12vw" },
                                }}
                                placeholder={t(
                                    "views.home.components.tax_submissions_table_header_filters.operator.placeholder",
                                )}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        padding: 0,
                                        paddingLeft: "4px",
                                    },
                                }}
                            />
                        )}
                        style={{ width: "12vw" }}
                        value={selectedOperator}
                    />
                    <Autocomplete
                        disableClearable={false}
                        disabled={
                            isFetchingSubjects ||
                            intermediaryOptions.length === 0
                        }
                        loading={isFetchingSubjects}
                        getOptionLabel={(option: Intermediary) =>
                            `${option.taxId} ${option.siteCode}`
                        }
                        isOptionEqualToValue={(option, value) => {
                            return option.taxId === value.taxId;
                        }}
                        noOptionsText={t(
                            "views.home.components.tax_submissions_table_header_filters.intermediary.no_options_text",
                        )}
                        onChange={handleIntermediaryChange}
                        options={intermediaryOptions}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t(
                                    "views.home.components.tax_submissions_table_header_filters.intermediary.label",
                                )}
                                placeholder={t(
                                    "views.home.components.tax_submissions_table_header_filters.intermediary.placeholder",
                                )}
                                InputLabelProps={{
                                    ...params.InputLabelProps,
                                    shrink: true,
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    sx: { width: "12vw" },
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        padding: 0,
                                        paddingLeft: "4px",
                                    },
                                }}
                            />
                        )}
                        style={{ width: "12vw" }}
                        value={selectedIntermediary}
                    />
                </Stack>
            )}
        </Stack>
    );
};
